import React, { useState, useEffect } from 'react';
import { createPortal } from 'react-dom';
import { ContentfulThemeApiClient } from '@jbhi-fi/apis/src/contentfulThemeApi/ContentfulThemeApiClient';
import { serviceHelper } from '../../../config/service-helper';
import { useContentfulTooltips } from '@jbhi-fi/components/src/hooks/useContentfulTooltips/useContentfulTooltips';
import { Tooltip } from '@jbhi-fi/lanyard-ui/components/Tooltip/Tooltip';
import { TooltipTransformer } from '@jbhi-fi/components/src/Contentful/transformers/TooltipTransformer';
import { getSession, setSession, SessionStorageKeys } from '@jbhi-fi/utils/src/sessionStorage';
import { styles } from './TooltipPortal.css';
import { ContentfulTooltip } from '@jbhi-fi/apis/src/contentful/types';
import { Tracker } from '@jbhi-fi/components/src/Tracking';

const contentfulClient = new ContentfulThemeApiClient({
  themeApiBaseUrl: serviceHelper.themeApisUrl,
});

export const TooltipPortal = () => {
  const container = window.document.getElementById('shopify-section-header');

  const [showLoginTooltip, setShowLoginTooltip] = useState(true);
  const [tooltipOpen, setTooltipOpen] = useState(true);

  const { tooltips } = useContentfulTooltips({ contentfulThemeApiClient: contentfulClient });

  const getTransformedTooltipProps = (tooltip: ContentfulTooltip) => {
    return TooltipTransformer.toTooltipProps(tooltip);
  };

  useEffect(() => {
    const nudgeTimer = tooltipOpen
      ? setTimeout(() => {
          setTooltipOpen(false);
        }, 7000) // Fold the tooltip after 7 seconds (5 seconds for the animation and 2 seconds for animation out)
      : setTimeout(() => {
          onTooltipHide();
        }, 7000); // Close the tooltip after 7 seconds (5 seconds for the animation and 2 seconds for animation in)

    return () => {
      clearTimeout(nudgeTimer);
    };
  }, [tooltipOpen]);

  const onTooltipSlideOut = () => {
    setTooltipOpen(true);
    recordNudgeSession();
  };

  const onTooltipHide = () => {
    setSession(SessionStorageKeys.HideLoginTooltip, 0);
    setShowLoginTooltip(false);
  };

  const recordNudgeSession = () => {
    //  The total animation duration is 10s + 4s transition time.
    setSession(SessionStorageKeys.HideLoginTooltip, Date.now() + 14000);
    Tracker.trackCustomEvent({ event: 'login_nudge' });
  };

  const handleLoginTooltip = () => {
    const hideLoginTooltip = getSession(SessionStorageKeys.HideLoginTooltip);
    if (!hideLoginTooltip) {
      recordNudgeSession();
    } else if (Date.now() > hideLoginTooltip) {
      onTooltipHide();
    }
  };

  const tooltipsList = tooltips?.map(tooltip => {
    const isLoginNudge = tooltip?.fields?.name === 'Log in nudge';
    if (isLoginNudge && tooltip?.fields) {
      tooltip.fields = {
        ...tooltip.fields,
        shouldHideArrow: true,
        accurateBorderRadius: '15px',
        transition: 'from-right',
        isAnimationEnabled: true,
        isTooltipOut: tooltipOpen,
      };
    }

    let children = <Tooltip {...getTransformedTooltipProps(tooltip)} />;

    if (isLoginNudge && tooltip?.fields?.isAnimationEnabled) {
      if (showLoginTooltip) {
        handleLoginTooltip();
        children = (
          <div className={styles.nudge} onMouseEnter={() => onTooltipSlideOut()} role="button" tabIndex={0}>
            {children}
          </div>
        );
      } else return null;
    }
    const anchorElement = tooltip?.fields?.elementClass;
    if (!anchorElement || !document.querySelector(anchorElement)) return null;

    return (
      <span className={styles.tooltipContainer} key={tooltip.sys.id}>
        {children}
      </span>
    );
  });

  if (!container) return null;

  return <>{createPortal(tooltipsList, container)}</>;
};
